import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  CircularProgress,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { collection, addDoc, deleteDoc, doc} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../tenantloader/AuthContextProvider';
import { useData } from '../DataProvider';
import { BEGINNING_MARKER, ENDING_MARKER } from '../constants';

/**
 * Parse ActionTime from click image filename (YYMMDDHHMMSS-mmm format)
 * Returns ISO string or null if parsing fails
 */
function parseActionTimeFromFilename(filename) {
  if (!filename) return null;
  
  // Remove 'm' suffix if manual capture
  const baseFileName = filename.endsWith('m') ? filename.slice(0, -1) : filename;
  
  // Parse: YYMMDDHHMMSS-mmm
  const match = baseFileName.match(/^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})-(\d{3})$/);
  if (!match) return null;
  
  try {
    const [, yy, mm, dd, hh, min, ss, ms] = match;
    const year = 2000 + parseInt(yy, 10);
    const date = new Date(Date.UTC(
      year, 
      parseInt(mm, 10) - 1, 
      parseInt(dd, 10),
      parseInt(hh, 10), 
      parseInt(min, 10), 
      parseInt(ss, 10), 
      parseInt(ms, 10)
    ));
    
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  } catch (e) {
    return null;
  }
}

const ImageAnnotation = ({ open, onClose, imageData, appInfo, runId = null, allAnnotations = [], viewConfig = {} }) => {
  const allowAnnotationForm = !!viewConfig?.annotationForm;
  const allowShowExistingAnnotations = !!viewConfig?.showExistingAnnotations;
  const [type, setType] = useState('intent');
  const [note, setNote] = useState('');
  const [scenario, setScenario] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Get current user from auth context
  const { currentUser } = useAuth();
  
  // Get hints data for scenarios
  const { hints } = useData();
  
  // Extract unique scenarios from hints data
  const scenarios = React.useMemo(() => {
    const scenarioSet = new Set();
    hints.forEach(hint => {
      if (hint.scenarios && Array.isArray(hint.scenarios)) {
        hint.scenarios.forEach(scenario => scenarioSet.add(scenario));
      }
    });
    return Array.from(scenarioSet).sort();
  }, [hints]);
  
  // Filter annotations for the current image (including virtual markers)
  const annotations = allAnnotations.filter(ann => ann.Image === imageData?.clickImageFileName);

  const handleSave = async () => {
    if (!note.trim()) return;
    
    if (!runId) {
      console.error('Cannot save annotation: runId is not available');
      return;
    }
    
    if (!currentUser) {
      console.error('Cannot save annotation: user is not authenticated');
      setError('You must be logged in to save annotations');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Get ActionTime with fallback: event.ActionTime -> parsed from filename -> current time
      let actionTime = imageData?.event?.ActionTime;
      if (!actionTime && imageData?.clickImageFileName) {
        actionTime = parseActionTimeFromFilename(imageData.clickImageFileName);
      }
      if (!actionTime) {
        actionTime = new Date().toISOString(); // Last resort fallback
      }
      
      // Get VideoOffset with fallback to 0 if not available
      let videoOffset = imageData?.event?.VideoOffset;
      if (typeof videoOffset !== 'number') {
        videoOffset = 0;
      }
      
      const annotationData = {
        Type: type,
        Note: note.trim(),
        Scenario: scenario,
        ActionTime: actionTime,
        VideoOffset: videoOffset,
        Image: imageData?.clickImageFileName,
        InspectionRunId: runId,
        CreatedAt: new Date().toISOString(),
        CreatedBy: currentUser?.email || 'unknown-user'
      };
      
      // Check if annotation already exists for this image, type, and scenario
      const annotationsRef = collection(db, 'annotation');
      await addDoc(annotationsRef, annotationData);
      
      handleClose();
    } catch (err) {
      console.error('Error saving annotation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Pre-populate form when editing existing annotation - only when modal opens
  React.useEffect(() => {
    if (open && type) {
      if (annotations.length > 0) {
        const existingAnnotation = annotations.find(ann => ann.Type === type);
        if (existingAnnotation) {
          setNote(existingAnnotation.Note || '');
          setScenario(existingAnnotation.Scenario || '');
        } else {
          setNote('');
          setScenario('');
        }
      } else {
        setNote('');
        setScenario('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Only run when modal opens, not on every type/annotation change

  // Handle note input changes
  const handleNoteChange = (e) => {
    setNote(e.target.value);
  };

  const handleDeleteAnnotation = async (annotationId) => {
    if (!runId || !currentUser) {
      console.error('Cannot delete annotation: runId or user not available');
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await deleteDoc(doc(db, 'annotation', annotationId));
    } catch (err) {
      console.error('Error deleting annotation:', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    setNote('');
    setScenario('');
    setType('');
    setError(null);
    onClose();
  };

  if (!imageData) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add Annotation
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Video Offset: {typeof imageData.event?.VideoOffset === 'number' ? `${imageData.event.VideoOffset}ms` : 'N/A'}
          </Typography>
          {(() => {
            // Display ActionTime with fallback from filename
            const actionTime = imageData.event?.ActionTime || parseActionTimeFromFilename(imageData?.clickImageFileName);
            if (actionTime) {
              return (
                <Typography variant="body2" color="text.secondary">
                  Action Time: {new Date(actionTime).toLocaleString()}
                  {!imageData.event?.ActionTime && ' (from filename)'}
                </Typography>
              );
            }
            return null;
          })()}
          {imageData.event?.ElementControlType && (
            <Typography variant="body2" color="text.secondary">
              Control: {imageData.event.ElementControlType}
            </Typography>
          )}
          {imageData.event?.isManualCapture && (
            <Typography variant="body2" color="info.main" sx={{ fontStyle: 'italic' }}>
              Manual Capture
            </Typography>
          )}
          {(imageData.clickImageFileName === BEGINNING_MARKER || imageData.clickImageFileName === ENDING_MARKER) && (
            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'bold', mt: 1 }}>
              {imageData.clickImageFileName === BEGINNING_MARKER ? 'Beginning' : 'Ending'} Event Marker
            </Typography>
          )}
        </Box>

        {!allowAnnotationForm && !runId && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="body2" color="warning.contrastText">
              Annotations are not available for this inspection
            </Typography>
          </Box>
        )}

        {!allowAnnotationForm && !currentUser && runId && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography variant="body2" color="error.contrastText">
              You must be logged in to save annotations
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography variant="body2" color="error.contrastText">
              Error: {error}
            </Typography>
          </Box>
        )}

        {allowShowExistingAnnotations && annotations.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Existing Annotations:
            </Typography>
            {annotations.map((annotation, index) => (
              <Box key={annotation.id || index} sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {annotation.Type}: {annotation.Note}
                  </Typography>
                  {annotation.Scenario && (
                    <Typography variant="body2" color="text.secondary">
                      Scenario: {annotation.Scenario}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Created: {new Date(annotation.CreatedAt).toLocaleString()}
                  </Typography>
                </Box>
                {allowAnnotationForm && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteAnnotation(annotation.id)}
                    disabled={deleting || !runId || !currentUser}
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}

        {allowShowExistingAnnotations && !allowAnnotationForm && annotations.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No annotations for this image.
          </Typography>
        )}

        {allowAnnotationForm && (
          <>
            <FormControl fullWidth sx={{ mb: 2 }} required>
              <InputLabel>Type</InputLabel>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value)}
                label="Type"
                disabled={!runId}
              >
                <MenuItem value="">Select a type</MenuItem>
                <MenuItem value="intent">Intent</MenuItem>
                <MenuItem value="observation">Observation</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }} required>
              <InputLabel>Scenario</InputLabel>
              <Select
                value={scenario}
                onChange={(e) => setScenario(e.target.value)}
                label="Scenario"
                disabled={!runId}
              >
                <MenuItem value="">
                </MenuItem>
                {scenarios.length > 0 ? scenarios.map((scenarioOption) => (
                  <MenuItem key={scenarioOption} value={scenarioOption}>
                    {scenarioOption}
                  </MenuItem>
                )) : (
                  <MenuItem value="" disabled>
                    <em>No scenarios available</em>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Annotation"
              value={note}
              onChange={handleNoteChange}
              placeholder={runId ? "Enter your annotation..." : "Annotations not available"}
              disabled={!runId}
              required
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{!allowAnnotationForm ? 'Close' : 'Cancel'}</Button>
        {allowAnnotationForm && (
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!type.trim() || !note.trim() || !scenario.trim() || !runId || !currentUser || loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImageAnnotation;
